import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Building2, Briefcase, Calendar, Tag, Users, Star, HandHelping, CheckCircle, Mail } from 'lucide-react';

const API = '/api';

const TYPE_CONFIG = {
  business:           { icon: Building2,    color: 'text-blue-400',    dot: 'bg-blue-400' },
  job:                { icon: Briefcase,    color: 'text-green-400',   dot: 'bg-green-400' },
  event:              { icon: Calendar,     color: 'text-purple-400',  dot: 'bg-purple-400' },
  classified:         { icon: Tag,          color: 'text-amber-400',   dot: 'bg-amber-400' },
  signup:             { icon: Users,        color: 'text-rose-400',    dot: 'bg-rose-400' },
  review:             { icon: Star,         color: 'text-yellow-400',  dot: 'bg-yellow-400' },
  request_posted:     { icon: HandHelping,  color: 'text-orange-400',  dot: 'bg-orange-400' },
  request_fulfilled:  { icon: CheckCircle,  color: 'text-teal-400',    dot: 'bg-teal-400' },
  newsletter:         { icon: Mail,         color: 'text-sky-400',     dot: 'bg-sky-400' },
};

const timeAgo = (isoString) => {
  if (!isoString) return '';
  const diff = Math.floor((Date.now() - new Date(isoString)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(isoString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const ActivityTicker = () => {
  const [activities, setActivities] = useState([]);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/activity-feed`)
      .then(res => setActivities(res.data.activities || []))
      .catch(() => {});
  }, []);

  if (activities.length === 0) return null;

  // Duplicate items for seamless loop
  const items = [...activities, ...activities];
  const duration = Math.max(30, activities.length * 4); // ~4s per item

  return (
    <div
      className="bg-[#0a1f2e] border-y border-white/5 py-2.5 overflow-hidden select-none"
      data-testid="activity-ticker"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Leading label */}
      <div className="flex items-center">
        <div className="flex-shrink-0 flex items-center gap-2 px-4 border-r border-white/10 mr-4">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
          <span className="text-xs font-semibold text-white/60 uppercase tracking-widest whitespace-nowrap">Live <span className="hidden sm:inline">Activity</span></span>
        </div>

        {/* Scrolling track */}
        <div className="flex-1 overflow-hidden relative">
          <div
            ref={trackRef}
            className="flex items-center gap-8 whitespace-nowrap"
            style={{
              animation: `ticker-scroll ${duration}s linear infinite`,
              animationPlayState: paused ? 'paused' : 'running',
              width: 'max-content',
            }}
          >
            {items.map((activity, idx) => {
              const cfg = TYPE_CONFIG[activity.type] || TYPE_CONFIG.business;
              const Icon = cfg.icon;
              return (
                <span key={idx} className="inline-flex items-center gap-2 text-sm text-white/70">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                  <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${cfg.color}`} />
                  <span>{activity.message}</span>
                  {activity.created_at && (
                    <span className="text-white/35 text-xs">{timeAgo(activity.created_at)}</span>
                  )}
                  {/* Separator dot between items */}
                  <span className="text-white/20 ml-4">·</span>
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(50vw); }
          100% { transform: translateX(calc(50vw - 50%)); }
        }
      `}</style>
    </div>
  );
};

export default ActivityTicker;
